import { Link } from 'react-router-dom';
import { useSettings } from '../../../hooks/useSettings';
import { useInView } from '../../../hooks/useInView';

interface StaticService {
  icon: string;
  desc: string;
  items: string[];
  img: string;
}

const STATIC_MAP: Record<string, StaticService> = {
  'Revisiones periódicas': {
    icon: 'ri-settings-3-line',
    desc: 'Mantenimiento completo siguiendo el plan del fabricante: aceite, filtros, correa de distribución, líquidos y más.',
    items: ['Cambio de aceite y filtros', 'Inspección de frenos', 'Verificación de fluidos', 'Revisión de neumáticos'],
    img: 'https://public.readdy.ai/ai/img_res/f244378336aed4324beea7e6f5193d61.jpg',
  },
  'Diagnóstico electrónico': {
    icon: 'ri-search-eye-line',
    desc: 'Detectamos cualquier fallo con nuestros equipos de última generación. OBD avanzado para todas las marcas.',
    items: ['Lectura de códigos de error', 'Análisis de sensores', 'Informe detallado', 'Diagnóstico de motor'],
    img: 'https://readdy.ai/api/search-image?query=automotive%20electronic%20diagnostic%20laptop%20OBD%20scanner%20connected%20car%20computer%20mechanic%20reading%20error%20codes%20professional%20workshop%20dark%20background%20glowing%20screen&width=600&height=400&seq=srv_diag2&orientation=landscape',
  },
  'Frenos y suspensión': {
    icon: 'ri-car-line',
    desc: 'Tu seguridad es lo primero. Revisamos y sustituimos pastillas, discos, amortiguadores y rótulas.',
    items: ['Cambio de pastillas y discos', 'Amortiguadores', 'Rótulas y silentblocks', 'Líquido de frenos'],
    img: 'https://readdy.ai/api/search-image?query=automotive%20brake%20disc%20caliper%20close%20up%20car%20brake%20system%20repair%20professional%20mechanic%20workshop%20dark%20industrial%20background%20dramatic%20lighting%20high%20detail&width=600&height=400&seq=srv_frenos2&orientation=landscape',
  },
  'Chapa y pintura': {
    icon: 'ri-paint-brush-line',
    desc: 'Restauramos tu vehículo a su estado original con técnicas de reparación y pintura profesional.',
    items: ['Reparación de abolladuras', 'Pintura al horno', 'Pulido y abrillantado', 'Tratamiento anticorrosión'],
    img: 'https://readdy.ai/api/search-image?query=car%20bodywork%20paint%20spray%20professional%20automotive%20painter%20spraying%20car%20body%20workshop%20clean%20dark%20background%20reflective%20metallic%20surface&width=600&height=400&seq=srv_chapa2&orientation=landscape',
  },
  'Climatización': {
    icon: 'ri-temp-cold-line',
    desc: 'Mantenimiento y reparación de sistemas de aire acondicionado y calefacción para mayor confort.',
    items: ['Recarga de gas refrigerante', 'Limpieza de circuito', 'Sustitución de compresor', 'Diagnóstico AC'],
    img: 'https://readdy.ai/api/search-image?query=car%20climate%20control%20system%20mechanic%20automotive%20workshop%20dark%20background%20cool%20air%20temperature%20control%20vehicle%20hvac%20professional%20service&width=600&height=400&seq=srv_clima2&orientation=landscape',
  },
  'Motos': {
    icon: 'ri-riding-line',
    desc: 'También atendemos motocicletas de todas las marcas. Revisiones, reparaciones y puesta a punto.',
    items: ['Revisión completa moto', 'Cambio de aceite y filtros', 'Frenos y suspensión', 'Electricidad y batería'],
    img: 'https://readdy.ai/api/search-image?query=motorcycle%20mechanic%20repairing%20motorbike%20engine%20workshop%20dark%20background%20professional%20tools%20sport%20bike%20maintenance%20service%20clean%20garage&width=600&height=400&seq=srv_moto2&orientation=landscape',
  },
  'Aire acondicionado': {
    icon: 'ri-temp-cold-line',
    desc: 'Servicio completo de aire acondicionado: carga de gas, limpieza del circuito, detección de fugas y reparación.',
    items: ['Recarga de gas refrigerante', 'Detección y reparación de fugas', 'Limpieza del evaporador', 'Sustitución de compresor'],
    img: 'https://readdy.ai/api/search-image?query=car%20air%20conditioning%20recharge%20mechanic%20connecting%20refrigerant%20gas%20machine%20to%20vehicle%20AC%20port%20professional%20workshop%20dark%20background%20hvac%20automotive%20service%20equipment&width=600&height=400&seq=srv_ac2&orientation=landscape',
  },
  'Transferencias y bajas temporales': {
    icon: 'ri-file-transfer-line',
    desc: 'Nos encargamos de todo el papeleo: transferencias de vehículo, bajas temporales y trámites con la DGT.',
    items: ['Transferencia de titularidad', 'Baja temporal del vehículo', 'Gestión con la DGT', 'Asesoramiento completo'],
    img: 'https://readdy.ai/api/search-image?query=vehicle%20registration%20transfer%20official%20documents%20car%20keys%20paperwork%20on%20desk%20Spain%20DGT%20administrative%20automotive%20service%20clean%20minimal%20dark%20background&width=600&height=400&seq=srv_trf2&orientation=landscape',
  },
  'Reprogramación de centralitas': {
    icon: 'ri-cpu-line',
    desc: 'Reprogramación y reparación de ECU y centralitas. Actualizaciones de software y resolución de fallos electrónicos.',
    items: ['Reprogramación de ECU', 'Reparación de centralitas dañadas', 'Actualización de firmware', 'Borrado de errores permanentes'],
    img: 'https://readdy.ai/api/search-image?query=ECU%20car%20computer%20chip%20reprogramming%20automotive%20technician%20laptop%20connected%20vehicle%20control%20unit%20dark%20workshop%20background%20glowing%20circuit%20board%20electronic%20tuning%20close%20up&width=600&height=400&seq=srv_ecu2&orientation=landscape',
  },
  'Reparación de llaves y bombines': {
    icon: 'ri-key-line',
    desc: 'Reparación de mandos a distancia, llaves de corte láser y bombines del vehículo. Todas las marcas.',
    items: ['Reparación de mando a distancia', 'Sustitución de bombín', 'Codificación de llaves', 'Apertura de emergencia'],
    img: 'https://readdy.ai/api/search-image?query=car%20key%20transponder%20remote%20fob%20repair%20automotive%20locksmith%20workshop%20dark%20background%20lock%20cylinder%20tools%20security%20vehicle%20key%20programming%20close%20up%20detailed&width=600&height=400&seq=srv_key2&orientation=landscape',
  },
  'Copia de llaves': {
    icon: 'ri-key-2-line',
    desc: 'Copia y duplicado de llaves de coche con chip transponder, mandos y llaves de corte láser para todas las marcas.',
    items: ['Copia de llaves con chip', 'Duplicado de mando a distancia', 'Llaves de corte láser', 'Programación de transponder'],
    img: 'https://readdy.ai/api/search-image?query=car%20key%20cutting%20duplication%20machine%20workshop%20professional%20automotive%20locksmith%20new%20transponder%20chip%20dark%20background%20modern%20equipment%20copy%20service%20close%20up&width=600&height=400&seq=srv_keycopy2&orientation=landscape',
  },
  'Pre ITV': {
    icon: 'ri-file-check-2-line',
    desc: 'Te preparamos para superar la ITV a la primera. Revisamos todo lo que comprueba la estación de ITV para que no haya sorpresas.',
    items: ['Revisión de luces y ópticas', 'Comprobación de emisiones', 'Frenos, suspensión y dirección', 'Documentación y etiquetas al día'],
    img: 'https://readdy.ai/api/search-image?query=mechanic%20performing%20pre%20ITV%20technical%20vehicle%20inspection%20checklist%20professional%20automotive%20technician%20checking%20car%20lights%20emissions%20brakes%20dark%20garage%20workshop%20background%20dramatic&width=600&height=400&seq=srv_preitv&orientation=landscape',
  },
  'Gestión de ITV': {
    icon: 'ri-shield-check-line',
    desc: 'Pasamos la ITV por ti. Recogemos tu vehículo, gestionamos la cita y te lo devolvemos con la ITV pasada. Sin esperas ni molestias.',
    items: ['Recogida y entrega del vehículo', 'Gestión de cita en la ITV', 'Acompañamiento durante la inspección', 'Resolución de defectos leves in situ'],
    img: 'https://readdy.ai/api/search-image?query=ITV%20technical%20vehicle%20inspection%20station%20Spain%20car%20passing%20official%20inspection%20professional%20service%20dark%20background%20modern%20facility%20clean%20exterior%20vehicle%20queue&width=600&height=400&seq=srv_gstitv&orientation=landscape',
  },
  'Luz ambiental LED': {
    icon: 'ri-lightbulb-flash-line',
    desc: 'Instalación de iluminación LED decorativa de interior: salpicadero, consola central, puertas y huecos. Transforma el ambiente de tu coche.',
    items: ['LED en salpicadero y consola', 'Iluminación de puertas y huecos', 'RGB con app o mando a distancia', 'Instalación limpia sin dañar el vehículo'],
    img: 'https://readdy.ai/api/search-image?query=car%20interior%20ambient%20LED%20lighting%20glowing%20dashboard%20door%20panels%20console%20colorful%20RGB%20strip%20lights%20luxury%20vehicle%20dark%20interior%20atmospheric%20mood%20lighting%20close%20up%20detail&width=600&height=400&seq=srv_ledamb&orientation=landscape',
  },
  'Caja de cambios': {
    icon: 'ri-settings-2-line',
    desc: 'Reparación de cajas de cambios manuales y automáticas. También realizamos el mantenimiento completo de cajas automáticas: cambio de aceite, filtros y revisión de componentes.',
    items: ['Reparación de caja manual y automática', 'Mantenimiento de caja automática', 'Cambio de aceite y filtro ATF', 'Diagnóstico electrónico de cambio'],
    img: 'https://readdy.ai/api/search-image?query=automatic%20transmission%20gearbox%20repair%20disassembled%20professional%20mechanic%20automotive%20workshop%20dark%20industrial%20background%20detailed%20internal%20components%20torque%20converter%20clutch%20plates%20technical%20service&width=600&height=400&seq=srv_gearbox&orientation=landscape',
  },
  'Filtro de partículas': {
    icon: 'ri-filter-3-line',
    desc: 'Limpieza con ultrasonido del filtro de partículas (DPF/FAP) con desmontaje incluido. También realizamos regeneración y reconstrucción de filtros para devolver el rendimiento original al motor.',
    items: ['Limpieza con ultrasonido (desmontaje)', 'Regeneración de filtros de partículas', 'Reconstrucción de filtros', 'Borrado del testigo de avería'],
    img: 'https://readdy.ai/api/search-image?query=diesel%20particulate%20filter%20DPF%20removed%20disassembled%20ultrasonic%20cleaning%20bath%20industrial%20tank%20automotive%20workshop%20dark%20background%20professional%20emission%20filter%20reconstruction%20service%20high%20detail&width=600&height=400&seq=srv_dpf_ultra&orientation=landscape',
  },
  'Solución AdBlue': {
    icon: 'ri-drop-line',
    desc: 'Diagnóstico, reparación y solución de problemas del sistema AdBlue (SCR). Resolvemos fallos de inyector, sensor, bomba y más.',
    items: ['Diagnóstico del sistema SCR', 'Reparación de inyector AdBlue', 'Sustitución de bomba y sensor', 'Rellenado y testigo de avería'],
    img: 'https://readdy.ai/api/search-image?query=AdBlue%20DEF%20fluid%20automotive%20system%20mechanic%20refilling%20urea%20tank%20diesel%20vehicle%20dark%20workshop%20background%20professional%20technical%20service%20emission%20control%20close%20up%20blue%20fluid%20container&width=600&height=400&seq=srv_adblue&orientation=landscape',
  },
  'Revisión 25 puntos': {
    icon: 'ri-shield-star-line',
    desc: 'Revisión gratuita de 25 puntos solo por visitar nuestras instalaciones. Comprobamos luces, niveles, suspensión, frenos, correa, embrague, silentblocks y mucho más. Sin compromiso.',
    items: ['Revisión de luces y ópticas', 'Niveles de fluidos y frenos', 'Suspensión, silentblocks y rótulas', 'Correa de distribución y embrague'],
    img: 'https://readdy.ai/api/search-image?query=mechanic%20performing%20thorough%2025%20point%20vehicle%20inspection%20checklist%20clipboard%20professional%20automotive%20technician%20workshop%20dark%20background%20free%20inspection%20service%20lights%20brakes%20suspension%20detailed%20check&width=600&height=400&seq=srv_rev25&orientation=landscape',
  },
};

const DEFAULT_STATIC: StaticService = {
  icon: 'ri-tools-line',
  desc: 'Servicio profesional realizado por nuestro equipo de técnicos especializados.',
  items: ['Presupuesto sin compromiso', 'Trabajo garantizado', 'Atención personalizada'],
  img: 'https://readdy.ai/api/search-image?query=professional%20mechanic%20working%20on%20car%20engine%20automotive%20workshop%20dark%20background%20tools%20professional%20service%20quality%20repair%20garage&width=600&height=400&seq=srv_default&orientation=landscape',
};

const ServiciosGrid = () => {
  const { settings, loading } = useSettings();
  const [gridRef, gridInView] = useInView(0.05);

  const servicios = settings.servicios.length > 0 ? settings.servicios : [
    { nombre: 'Revisiones periódicas', precio: 'Desde 49 €' },
    { nombre: 'Diagnóstico electrónico', precio: 'Desde 29 €' },
    { nombre: 'Frenos y suspensión', precio: 'Desde 79 €' },
    { nombre: 'Chapa y pintura', precio: 'Presupuesto' },
    { nombre: 'Climatización', precio: 'Desde 59 €' },
    { nombre: 'Motos', precio: 'Desde 39 €' },
    { nombre: 'Aire acondicionado', precio: 'Desde 59 €' },
    { nombre: 'Transferencias y bajas temporales', precio: 'Consultar' },
    { nombre: 'Reprogramación de centralitas', precio: 'Desde 80 €' },
    { nombre: 'Reparación de llaves y bombines', precio: 'Desde 45 €' },
    { nombre: 'Copia de llaves', precio: 'Desde 25 €' },
    { nombre: 'Pre ITV', precio: 'Desde 29 €' },
    { nombre: 'Gestión de ITV', precio: 'Consultar' },
    { nombre: 'Luz ambiental LED', precio: 'Desde 89 €' },
    { nombre: 'Caja de cambios', precio: 'Desde 69 €' },
    { nombre: 'Filtro de partículas', precio: 'Desde 79 €' },
    { nombre: 'Solución AdBlue', precio: 'Consultar' },
    { nombre: 'Revisión 25 puntos', precio: 'GRATIS' },
  ];

  if (loading) {
    return (
      <section className="bg-zinc-950 py-20 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-40">
          <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-zinc-950 py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {servicios.map((s, idx) => {
            const staticData = STATIC_MAP[s.nombre] ?? DEFAULT_STATIC;
            const img = (s as typeof s & { imagen?: string }).imagen || staticData.img;
            const hasOffer = (s as typeof s & { oferta_activa?: boolean; oferta?: string }).oferta_activa && (s as typeof s & { oferta?: string }).oferta;

            return (
              <div
                key={s.nombre}
                className={`group bg-zinc-900/60 border border-zinc-800 hover:border-yellow-400/25 transition-all duration-500 overflow-hidden cursor-default ${
                  gridInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${idx * 0.08}s`, transitionDuration: '600ms' }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    alt={s.nombre}
                    src={img}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 text-black">
                      <i className={`${staticData.icon} text-sm`} />
                    </div>
                    {hasOffer && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-red-500 text-white tracking-wider">Oferta</span>
                    )}
                  </div>
                  <div className="absolute bottom-3 right-3">
                    {hasOffer ? (
                      <div className="text-right">
                        <span className="block text-zinc-400 text-[10px] line-through leading-none">{s.precio}</span>
                        <span className="block text-yellow-400 text-sm font-bold leading-tight">{(s as typeof s & { oferta?: string }).oferta}</span>
                      </div>
                    ) : (
                      <span className="bg-black/70 text-yellow-400 text-xs font-bold px-2 py-1">{s.precio}</span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-2">{s.nombre}</h3>
                  <p className="text-zinc-500 text-xs leading-relaxed mb-3 font-light">{staticData.desc}</p>
                  <ul className="space-y-1.5 mb-5">
                    {staticData.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-zinc-400">
                        <i className="ri-checkbox-circle-line text-yellow-400/60 text-xs flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <div>
                      {hasOffer ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-zinc-500 text-xs line-through">{s.precio}</span>
                          <span className="text-yellow-400 text-base font-bold">{(s as typeof s & { oferta?: string }).oferta}</span>
                        </div>
                      ) : (
                        <span className="text-yellow-400 text-sm font-bold">{s.precio}</span>
                      )}
                    </div>
                    <Link
                      to="/contacto"
                      className="group/btn inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Presupuesto
                      <i className="ri-arrow-right-line group-hover/btn:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiciosGrid;
